// i18n
import { translateInComponent } from 'infras/i18n';
import translations from './i18n.json';
import { ReactNode, useEffect } from 'react';
import styled from '@emotion/styled';
import ServicePageTemplate from 'presentations/views/components/templates/ServicePageTemplate';
import EventTypeHeader from 'presentations/views/components/organisms/EventTypeHeader';
import Button from 'presentations/views/components/atoms/Button';
import { ViewerItem } from 'presentations/api/schema/user';
import {
  spacing,
  mediaMax,
  fontSize,
  fontWeight,
} from 'presentations/views/styles';
import { FormPageItem } from 'presentations/api/schema/form';
import HeaderNotificationBar from 'presentations/views/components/molecules/HeaderNotificationBar';
import { formEditPath } from 'presentations/routes/path';
import FormFooter from 'presentations/views/components/organisms/FormFooter';
import { TeamItem } from 'presentations/api/schema/team';
import { useFormPage } from './hooks';
import FormFullscreenView from 'presentations/views/components/organisms/FormFullscreenView';
import FormListView from 'presentations/views/components/organisms/FormListView';
import { ListViewType } from 'domains/models/form/listViewType';
import { FormAnswerItem } from 'presentations/api/schema/form_answer';
import { primary } from 'presentations/views/styles/color';

export interface Props {
  viewer: ViewerItem | null;
  form: FormPageItem;
  formAnswer?: FormAnswerItem;
  team: TeamItem;
  breadcrumbs: ReactNode;
  isWidget?: boolean;
}

export default function Component(props: Props) {
  const t = translateInComponent(translations);

  const { state, actions } = useFormPage({
    viewer: props.viewer,
    form: props.form,
    formAnswer: props.formAnswer,
    team: props.team,
    headerTeamImage: props.team.headerTeamImage,
    isWidget: props.isWidget || false,
  });
  const isFullWidth = props.form.listViewType === ListViewType.FullScreen;

  return (
    <ServicePageTemplate
      header={
        props.isWidget ? null : (
          <EventTypeHeader
            viewer={props.viewer}
            headerTeamImage={props.team.headerTeamImage}
            isShowPromotion={props.form.isShowPromotion}
          />
        )
      }
      isHiddenFooter
      isHiddenContentFooter
      breadcrumbs={props.breadcrumbs}
      notificationBar={
        !props.isWidget &&
        state.isHost && (
          <HeaderNotificationBar
            notificationType={'info'}
            title={
              <HeaderTitle>
                <HostEditTitle>{t('HostEditTitle')}</HostEditTitle>
              </HeaderTitle>
            }
            link={formEditPath(props.form.uid)}
            linkTitle={t('Edit')}
          />
        )
      }
    >
      {!state.isShowForm ? (
        <DeactivatedMessage>
          {t('Deactivated Message')}
          <DeactivatedDescription>
            {t('Deactivated Description')}
          </DeactivatedDescription>
        </DeactivatedMessage>
      ) : isFullWidth ? (
        <FormFullscreenView
          viewer={props.viewer}
          form={props.form}
          team={props.team}
          onClickNext={actions.onClickNext}
          isLightColor={state.isLightColor}
          page={state.page}
          customQuestions={state.customQuestions}
          email={state.email}
          name={state.name}
          phoneNumber={state.phoneNumber}
          customQuestionAnswers={state.customQuestionAnswers}
          customOtherAnswers={state.customOtherAnswers}
          errors={state.errors}
          onSubmitForm={actions.onSubmitForm}
          onClickPrev={actions.onClickPrev}
          onChange={actions.onChange}
          onChangeOtherAnswer={actions.onChangeOtherAnswer}
          isComplete={state.isComplete}
          isLoading={state.isLoading}
          isDisabled={state.isDisabled}
          isWidget={props.isWidget}
        />
      ) : (
        <FormListView
          viewer={props.viewer}
          form={props.form}
          team={props.team}
          isLightColor={state.isLightColor}
          page={state.page}
          customQuestions={state.customQuestions}
          email={state.email}
          name={state.name}
          phoneNumber={state.phoneNumber}
          customQuestionAnswers={state.customQuestionAnswers}
          customOtherAnswers={state.customOtherAnswers}
          errors={state.errors}
          onSubmitForm={actions.onSubmitForm}
          onSubmit={actions.onSubmit}
          onChange={actions.onChange}
          onChangeOtherAnswer={actions.onChangeOtherAnswer}
          isLoading={state.isLoading}
          isDisabled={state.isDisabled}
          isWidget={props.isWidget}
        />
      )}
      {!props.isWidget && (
        <Footer>
          <FormFooter form={props.form} viewer={props.viewer} />
        </Footer>
      )}
    </ServicePageTemplate>
  );
}

const HeaderTitle = styled.div`
  display: flex;

  ${mediaMax.smartphone} {
    flex-direction: column;
  }
`;
const HostEditTitle = styled.span`
  line-height: 1.4;
  ${mediaMax.smartphone} {
    font-size: ${fontSize(12)};
  }
`;

const Footer = styled.div`
  margin: ${spacing(24)} auto;
  ${mediaMax.tablet} {
    width: 100%;
    padding: 0 ${spacing(12)};
  }
`;

const DeactivatedMessage = styled.div`
  margin: 0 ${spacing(48)};
  padding-top: ${spacing(32)};
  padding-bottom: ${spacing(48)};
  font-size: ${fontSize(20)};
  font-weight: ${fontWeight('bold')};
  text-align: center;

  ${mediaMax.tablet} {
    font-size: ${fontSize(14)};
  }
`;
const DeactivatedDescription = styled.div`
  font-size: ${fontSize(14)};
  font-weight: initial;
`;

const InquiryButton = styled(Button)`
  margin-top: ${spacing(16)};
  border-color: ${primary};
  font-size: ${fontSize(12)};
  font-weight: ${fontWeight('bold')};
`;
